/**
 * A town's terrain from its scene record (lifted from lantern-water's
 * `world.js` `createTown` and `terrace`, `quay-masonry.js` `quayStone`,
 * `stair-treads.js` and `terrace-masonry.js`): the quay blocks with their
 * paving, the raised terraces with their retaining walls, blind arcades,
 * railings, buttresses and stair flights, and the coping along the canal with
 * its posts and rail, open at the landing's stair and beside the bridges. The
 * scene record says where (`terrain`, the bridges in `water`); the shapes are
 * the prototype's. Game axes in the record (y north) become three.js's z = −y.
 *
 * The weathered canal masonry and the worn treads are the kit's stone and
 * cream with a shader addition each; their layout constants (the canal's
 * faces, the district heights, the stair flights' centres) are composed into
 * the GLSL from the record at build time, never patched afterwards.
 */
import type { Scene } from '@ab/content-schema';
import * as THREE from 'three';
import { addShaderHook } from '../material-hooks.ts';
import type { Ctx, Kit } from './kit.ts';

type Terrain = NonNullable<Scene['terrain']>;

const f = (v: number): string => v.toFixed(3);

/** The district height under a point in three.js axes (x, z), as GLSL (`p` a vec2 of x and z); 0 off the districts. */
export const districtGlsl = (terrain: Terrain, p: string): string =>
  terrain.districts.reduceRight(
    (rest, d) => `((${p}.x>=${f(d.x0)}&&${p}.x<${f(d.x1)}&&-${p}.y>=${f(d.y0)}&&-${p}.y<${f(d.y1)})?${f(d.h)}:${rest})`,
    '0.',
  );

/** The district height under a point in three.js axes. */
export const districtHeight = (terrain: Terrain, x: number, z: number): number => terrain.districts.find((d) => x >= d.x0 && x < d.x1 && -z >= d.y0 && -z < d.y1)?.h ?? 0;

/** The weathered canal masonry (`quay-masonry.js` `quayStone`): damp, algae and scupper streaks on the two river-facing walls. */
const quayStone = (kit: Kit, terrain: Terrain): THREE.Material =>
  kit.derive('terrain:quay-stone', kit.m.stone, (material) => {
    material.name = 'weathered-canal-masonry';
    addShaderHook(material, {
      key: 'town-quay-weather',
      vertexHeader: 'varying vec3 quayWorld;',
      fragmentHeader: 'varying vec3 quayWorld;',
      vertex: [{ chunk: 'worldpos_vertex', after: 'quayWorld=(modelMatrix*vec4(transformed,1.)).xyz;' }],
      fragment: [
        {
          chunk: 'color_fragment',
          after: `
vec3 q=floor(quayWorld*48.)/48.;
float face=1.-smoothstep(.08,.24,min(abs(q.x-${f(terrain.river.west)}),abs(q.x-${f(terrain.river.east)})));
float grain=sin(q.z*2.7+sin(q.z*7.3))*.035;
float damp=1.-smoothstep(-.75,.15,q.y+grain);
float algae=exp(-pow((q.y+.56+grain)/.24,2.));
float top=${districtGlsl(terrain, 'q.xz')};
float d=min(min(abs(q.z+15.2),abs(q.z+6.8)),min(abs(q.z-5.2),abs(q.z-15.6)));
float streak=exp(-pow(d/(.16+.055*max(top-q.y,0.)),2.));
streak*=(1.-smoothstep(top-.55,top-.2,q.y))*exp(-max(top-.5-q.y,0.)*.24);
float fleck=.75+.25*sin(q.z*21.+q.y*7.);
diffuseColor.rgb*=1.-face*(damp*.25+streak*fleck*.22);
diffuseColor.rgb=mix(diffuseColor.rgb,diffuseColor.rgb*vec3(.64,.78,.48),algae*face*.48);`,
        },
      ],
    });
    // The clone does not carry the stone's hooks: the quay walls shimmer with the canal's light (`quay-masonry.js`).
    addShaderHook(material, kit.shimmerHook);
  });

/** The worn stair limestone (`stair-treads.js` `treadStone`): staggered joints, chips and a worn edge band per flight. */
const treadStone = (kit: Kit, terrain: Terrain): THREE.Material =>
  kit.derive('terrain:tread-stone', kit.m.cream, (material) => {
    material.name = 'worn-stair-limestone';
    material.color.set('#d6cdbc');
    material.roughness = 0.9;
    const flights = (pick: (t: Terrain['terraces'][number]) => number): string =>
      terrain.terraces.reduceRight((rest, t) => `(treadWorld.x>=${f(t.x - t.width / 2)}&&treadWorld.x<${f(t.x + t.width / 2)})?${f(pick(t))}:${rest}`, f(pick(terrain.terraces[0] ?? { x: 0, width: 1, depth: 1, stairX: 0, stairWidth: 1, h: 0 })));
    addShaderHook(material, {
      key: 'town-worn-treads',
      vertexHeader: 'varying vec3 treadWorld;',
      fragmentHeader: 'varying vec3 treadWorld;',
      vertex: [{ chunk: 'worldpos_vertex', after: 'treadWorld=(modelMatrix*vec4(transformed,1.)).xyz;' }],
      fragment: [
        {
          chunk: 'color_fragment',
          after: `
float course=floor(treadWorld.y/.24+.45);
float shift=fract(sin(course*71.37)*437.19);
float stoneU=treadWorld.x/.86+shift;
float stone=floor(stoneU);
float variation=fract(sin(stone*63.1+course*17.3)*913.73);
float jointDistance=min(fract(stoneU),1.-fract(stoneU));
float aa=max(fwidth(stoneU),.002);
float joint=1.-smoothstep(.006,.006+aa,jointDistance);
float center=${flights((t) => t.stairX)};
float halfWidth=${flights((t) => t.stairWidth / 2)};
float edge=smoothstep(.69,.98,abs(treadWorld.x-center)/halfWidth);
vec3 q=floor(treadWorld*70.)/70.;
float mottling=sin(q.x*16.+sin(q.z*23.))*sin(q.z*41.)*.024;
float chips=step(.89,fract(sin(floor(stoneU*19.)*37.1+course*4.7)*971.3));
chips*=1.-smoothstep(.012,.044,jointDistance);
diffuseColor.rgb*=.94+variation*.10+mottling-edge*.14;
diffuseColor.rgb*=1.-joint*.39-chips*.13;
diffuseColor.rgb=mix(diffuseColor.rgb,diffuseColor.rgb*vec3(.88,.94,.79),edge*.2);`,
        },
      ],
    });
  });

/** One beveled tread whose flat top is the walking height (`stair-treads.js` `stairTread`). */
const stairTread = (parent: THREE.Object3D, material: THREE.Material, x: number, height: number, z: number, width: number, depth: number): void => {
  const thickness = 0.08;
  const profile = new THREE.Shape();
  profile.moveTo(-depth / 2, 0);
  profile.lineTo(depth / 2 - 0.026, 0);
  profile.lineTo(depth / 2, -0.023);
  profile.lineTo(depth / 2, -thickness + 0.012);
  profile.lineTo(depth / 2 - 0.016, -thickness);
  profile.lineTo(-depth / 2, -thickness);
  profile.closePath();
  const geo = new THREE.ExtrudeGeometry(profile, { depth: width, bevelEnabled: false, steps: 1, curveSegments: 1 });
  geo.rotateY(-Math.PI / 2);
  geo.translate(width / 2, 0, 0);
  const pos = geo.getAttribute('position');
  const uv = geo.getAttribute('uv');
  const normal = geo.getAttribute('normal');
  for (let i = 0; i < uv.count; i += 1) uv.setXY(i, (pos.getX(i) + x) / 0.9, Math.abs(normal.getY(i)) > 0.5 ? (pos.getZ(i) + z) / 0.9 : (pos.getY(i) + height) / 0.9);
  const mesh = new THREE.Mesh(geo, material);
  mesh.position.set(x, height, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
};

/** Shallow blind arcades on a retaining wall (`terrace-masonry.js`). */
const terraceMasonry = (ctx: Ctx, parent: THREE.Object3D, lo: number, hi: number, front: number, height: number): void => {
  const { m, kit } = ctx;
  const infill = kit.derive('terrain:arcade-infill', m.stone, (material) => {
    material.color.set('#828b83');
  });
  const add = (geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number): void => {
    const pos = geometry.getAttribute('position');
    const uv = geometry.getAttribute('uv') as THREE.BufferAttribute | undefined;
    if (uv !== undefined) for (let i = 0; i < uv.count; i += 1) uv.setXY(i, (pos.getX(i) + x) / 2.5, (pos.getY(i) + y) / 2.5);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    parent.add(mesh);
  };
  const block = (material: THREE.Material, x: number, y: number, z: number, w: number, h: number, d: number): void => {
    add(new THREE.BoxGeometry(w, h, d), material, x, y, z);
  };
  const radius = 0.59;
  const rim = 0.17;
  const bottom = 0.4;
  const top = height - 0.29;
  const spring = top - radius - rim;
  for (let x = lo + 1.22; x + radius + rim < hi - 0.2; x += 2.2) {
    const panel = new THREE.Shape();
    panel.moveTo(-radius, bottom);
    panel.lineTo(radius, bottom);
    panel.lineTo(radius, spring);
    panel.absarc(0, spring, radius, 0, Math.PI, false);
    panel.lineTo(-radius, bottom);
    panel.closePath();
    add(new THREE.ShapeGeometry(panel, 12), infill, x, 0, front + 0.019);
    for (let i = 0; i < 13; i += 1) {
      const a = (i * Math.PI) / 13 + 0.009;
      const b = ((i + 1) * Math.PI) / 13 - 0.009;
      const stone = new THREE.Shape();
      stone.moveTo(Math.cos(a) * radius, Math.sin(a) * radius);
      stone.lineTo(Math.cos(a) * (radius + rim), Math.sin(a) * (radius + rim));
      stone.absarc(0, 0, radius + rim, a, b, false);
      stone.lineTo(Math.cos(b) * radius, Math.sin(b) * radius);
      stone.absarc(0, 0, radius, b, a, true);
      stone.closePath();
      add(new THREE.ExtrudeGeometry(stone, { depth: i === 6 ? 0.14 : 0.11, bevelEnabled: false, steps: 1, curveSegments: 2 }), i === 6 ? m.cream : m.stone, x, spring, front + 0.045);
    }
    const courses = Math.ceil((spring - bottom) / 0.29);
    const course = (spring - bottom) / courses;
    for (const side of [-1, 1]) {
      for (let i = 0; i < courses; i += 1) block(m.stone, x + side * (radius + rim / 2), bottom + (i + 0.5) * course, front + 0.1, rim, course - 0.013, 0.13);
      block(m.cream, x + side * (radius + rim / 2), spring - 0.015, front + 0.125, 0.23, 0.085, 0.18);
    }
    block(m.stone, x, bottom - 0.035, front + 0.12, 1.66, 0.14, 0.24);
  }
};

/** A raised terrace: the block and its paving, the retaining wall either side of the stair, and the stair flight (`world.js` `terrace`). */
const terrace = (ctx: Ctx, parent: THREE.Object3D, terrain: Terrain, t: Terrain['terraces'][number]): void => {
  const { box, line, m } = ctx;
  const front = -terrain.terraceFront;
  const { x, width, depth, stairX, stairWidth, h: height } = t;
  const zc = front - depth / 2;
  box(parent, x, (height - 0.05) / 2, zc, width, height - 0.05, depth, quayStone(ctx.kit, terrain));
  box(parent, x, height - 0.025, zc, width, 0.05, depth, m.paving);
  const xmin = x - width / 2;
  const xmax = x + width / 2;
  for (const [lo, hi] of [
    [xmin, stairX - stairWidth / 2],
    [stairX + stairWidth / 2, xmax],
  ] as const) {
    const length = hi - lo;
    if (length <= 0) continue;
    box(parent, (lo + hi) / 2, height + 0.03, front, length, 0.14, 0.4, m.cream);
    terraceMasonry(ctx, parent, lo, hi, front, height);
    box(parent, (lo + hi) / 2, 0.23, front + 0.08, length, 0.23, 0.44, m.stone);
    box(parent, (lo + hi) / 2, height + 0.84, front, length, 0.07, 0.065, m.iron);
    box(parent, (lo + hi) / 2, height + 0.23, front, length, 0.055, 0.06, m.iron);
    for (let xx = lo + 0.1; xx < hi; xx += 0.31) box(parent, xx, height + 0.53, front, 0.04, 0.65, 0.04, m.iron);
    for (let xx = lo + 0.12; xx < hi; xx += 2.2) {
      box(parent, xx, height / 2, front + 0.04, 0.37, height, 0.5, m.stone);
      box(parent, xx, height + 0.48, front, 0.33, 0.9, 0.36);
      box(parent, xx, height + 0.98, front, 0.46, 0.12, 0.48, m.cream);
    }
  }
  const count = Math.round(height / 0.24);
  const tread = 0.36;
  const capThickness = 0.08;
  const bottom = front + count * tread;
  const treads = treadStone(ctx.kit, terrain);
  for (let i = 0; i < count; i += 1) {
    const z = bottom - (i + 0.5) * tread;
    const h = (height * (i + 1)) / count;
    // The cap owns the visible walking surface; the body ends below it (no coplanar faces).
    const bodyHeight = h - capThickness;
    box(parent, stairX, bodyHeight / 2, z, stairWidth, bodyHeight, tread + 0.006, m.stone);
    stairTread(parent, treads, stairX, h, z + 0.02, stairWidth + 0.05, tread + 0.025);
    for (const side of [-1, 1]) {
      const xx = stairX + side * (stairWidth / 2 + 0.13);
      box(parent, xx, (h + 0.22) / 2, z, 0.26, h + 0.22, tread + 0.01, m.stone);
      if (i % 2 === 0) box(parent, xx, h + 0.64, z, 0.055, 0.82, 0.055, m.iron);
    }
  }
  for (const side of [-1, 1]) {
    const xx = stairX + side * (stairWidth / 2 + 0.13);
    line(parent, [xx, 1.05, bottom - 0.1], [xx, height + 0.95, front], 0.075, m.iron);
    for (const [zz, yy] of [
      [bottom, 0],
      [front, height],
    ] as const) {
      box(parent, xx, yy + 0.5, zz, 0.42, 1, 0.44, m.stone);
      box(parent, xx, yy + 1.05, zz, 0.56, 0.13, 0.58, m.cream);
    }
  }
};

/** The terrain of a town: quays, terraces, the canal's coping (`bridges`: the crossings' y, game axes). */
export const buildTerrain = (ctx: Ctx, terrain: Terrain, bridges: readonly number[]): THREE.Group => {
  const root = new THREE.Group();
  root.name = 'terrain';
  const { box, m } = ctx;
  for (const q of terrain.quays) {
    const x = (q.x0 + q.x1) / 2;
    const z = -(q.y0 + q.y1) / 2;
    const w = q.x1 - q.x0;
    const d = q.y1 - q.y0;
    box(root, x, -0.7, z, w, 1.4, d, quayStone(ctx.kit, terrain));
    box(root, x, -0.04, z, w, 0.12, d, m.paving);
  }
  for (const t of terrain.terraces) terrace(ctx, root, terrain, t);
  // Coping and repeated masonry along the water, open at the landing's stair (compare full extents) and the bridges.
  const z0 = -Math.max(...terrain.quays.map((q) => q.y1));
  const z1 = -Math.min(...terrain.quays.map((q) => q.y0));
  const centres = bridges.map((y) => -y);
  const gap = terrain.river.landingGap;
  const mid = (terrain.river.west + terrain.river.east) / 2;
  for (const x of terrain.river.coping) {
    for (let z = z0; z < z1; z += 1.05) {
      if (x < mid && Math.abs(z + gap.y) < gap.half) continue;
      const quayHeight = districtHeight(terrain, x, z);
      box(root, x, quayHeight - 0.07, z, 0.53, 0.34, 1.01, m.cream);
      if (centres.every((center) => Math.abs(z - center) > 2.4)) {
        if (Math.round(z * 10) % 3 === 0) {
          box(root, x, quayHeight + 0.43, z, 0.28, 0.76, 0.28, m.stone);
          box(root, x, quayHeight + 0.84, z, 0.37, 0.1, 0.37, m.cream);
        }
        box(root, x, quayHeight + 0.5, z, 0.07, 0.08, 1.08, m.iron);
      }
    }
  }
  return root;
};
